import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { shareReplay, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/auth/auth.service';
import {
  CreateNotificationCategoryRequest,
  NotificationCategoryDto,
  UpdateNotificationCategoryRequest,
} from './notification-category.model';

@Injectable({ providedIn: 'root' })
export class NotificationCategoriesService {
  private readonly apiUrl = `${environment.cafler_backoffice_back_url}/v1/notification-categories`;

  private categoriesCache$?: Observable<NotificationCategoryDto[]>;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly translate: TranslateService
  ) {}

  list(forceRefresh = false): Observable<NotificationCategoryDto[]> {
    if (forceRefresh || !this.categoriesCache$) {
      this.categoriesCache$ = from(this.authService.getAccessToken()).pipe(
        switchMap((token) => {
          if (!token) {
            throw new Error('No token available');
          }
          return this.http.get<NotificationCategoryDto[]>(this.apiUrl, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }),
        shareReplay(1)
      );
    }
    return this.categoriesCache$;
  }

  getById(id: number): Observable<NotificationCategoryDto> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          throw new Error('No token available');
        }
        return this.http.get<NotificationCategoryDto>(`${this.apiUrl}/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      })
    );
  }

  create(request: CreateNotificationCategoryRequest): Observable<NotificationCategoryDto> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          throw new Error('No token available');
        }
        return this.http.post<NotificationCategoryDto>(this.apiUrl, request, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }),
      tap(() => this.invalidateCache())
    );
  }

  update(
    id: number,
    request: UpdateNotificationCategoryRequest
  ): Observable<NotificationCategoryDto> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          throw new Error('No token available');
        }
        return this.http.put<NotificationCategoryDto>(`${this.apiUrl}/${id}`, request, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }),
      tap(() => this.invalidateCache())
    );
  }

  delete(id: number): Observable<void> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          throw new Error('No token available');
        }
        return this.http.delete<void>(`${this.apiUrl}/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }),
      tap(() => this.invalidateCache())
    );
  }

  invalidateCache(): void {
    this.categoriesCache$ = undefined;
  }

  /**
   * Resolves the user-facing label for a category.
   * Strategy: try the i18n key `notifications.categories.<name-lowercased>` first
   * (covers the 11 seeded categories localized in 5 languages); fall back to the
   * backend `description` or `name` for custom categories created by SuperAdmin
   * that don't have a translation key yet.
   */
  resolveLabel(category: NotificationCategoryDto): string {
    const key = `notifications.categories.${category.name.toLowerCase()}`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) {
      return translated;
    }
    return category.description?.trim() || category.name;
  }
}
